import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './UserList.scss';

const roleLabelMap = {
  Owner: 'Y\u00f6netici',
  Employee: '\u00c7al\u0131\u015fan',
  Company: 'Firma Kullan\u0131c\u0131s\u0131',
  Partner: 'Partner',
  Accountant: 'Muhasebe',
  RentalPersonnel: 'Kiralama Personeli',
  RentalAccountant: 'Kiralama Muhasebesi',
  TechnicalServiceAccountant: 'Teknik Servis Muhasebesi',
  TechnicalServiceManager: 'Yetkili Servis M\u00fcd\u00fcr\u00fc',
  TechnicalServicePersonnel: 'Yetkili Servis Personeli'
};

const hiddenRoleNames = ['Partner', 'Employee', 'RentalAccountant', 'TechnicalServiceAccountant'];

const roleFilterOptions = [
  { value: 'all', label: 'T\u00fcm Roller' },
  { value: 'accounting', label: 'Muhasebe' },
  { value: 'technical', label: 'Teknik Servis' },
  { value: 'company', label: 'Firma Kullan\u0131c\u0131lar\u0131' },
  { value: 'internal', label: '\u0130\u00e7 Kullan\u0131c\u0131lar' }
];

const roleDisplayOrder = ['Owner', 'Accountant', 'RentalPersonnel', 'TechnicalServiceManager', 'TechnicalServicePersonnel', 'Company'];

const pageSize = 10;

const emptyForm = {
  name: '',
  surname: '',
  roleId: '',
  companyId: '',
  tc: '',
  email: '',
  phoneNumber: '',
  password: ''
};

const UserList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  const getUserFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Token çözümlenemedi:', error);
      return null;
    }
  };

  const getClaimValue = (session, claimName) => {
    if (!session) return '';

    return (
      session[claimName] ||
      session[claimName.toLowerCase()] ||
      session[claimName.toUpperCase()] ||
      Object.entries(session).find(([key]) => key.toLowerCase().endsWith(claimName.toLowerCase()))?.[1] ||
      ''
    );
  };

  const sortedRoles = useMemo(() => {
    return roles
      .filter((role) => !hiddenRoleNames.includes(role.name))
      .sort((a, b) => {
      const orderA = roleDisplayOrder.indexOf(a.name);
      const orderB = roleDisplayOrder.indexOf(b.name);

      if (orderA !== -1 || orderB !== -1) {
        if (orderA === -1) return 1;
        if (orderB === -1) return -1;
        return orderA - orderB;
      }

      const labelA = roleLabelMap[a.name] || a.name;
      const labelB = roleLabelMap[b.name] || b.name;
      return labelA.localeCompare(labelB, 'tr');
      });
  }, [roles]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === formData.roleId),
    [roles, formData.roleId]
  );

  const isCompanyUserRole = selectedRole?.name === 'Company';

  const fetchUsersAndRoles = async () => {
    const session = getUserFromToken();
    const companyId = getClaimValue(session, 'CompanyId');

    if (!companyId) {
      throw new Error('Token i\u00e7inde CompanyId bulunamad\u0131.');
    }

    const [usersResult, rolesResult, companiesResult] = await Promise.allSettled([
      api.get(`/User?tenantId=${companyId}`),
      api.get('/Auth/GetAllRoles'),
      api.get('/Company')
    ]);

    const fetchedUsers = usersResult.status === 'fulfilled' ? usersResult.value?.data?.data || [] : [];
    const fetchedRoles = rolesResult.status === 'fulfilled' ? rolesResult.value?.data?.data || [] : [];
    const fetchedCompanies = companiesResult.status === 'fulfilled' ? companiesResult.value?.data?.data || [] : [];

    if (usersResult.status === 'rejected') {
      throw usersResult.reason;
    }

    const usersWithMeta = fetchedUsers.map((user) => {
      const resolvedRoleName = user.role || user.Role || '';
      const role = fetchedRoles.find((item) => item.id === user.roleId || item.id === user.RoleId || item.name === resolvedRoleName);
      const resolvedCompanyId = user.companyId || user.CompanyId || user.tenantId || user.TenantId || '';
      const company = fetchedCompanies.find((item) => item.id === resolvedCompanyId);

      return {
        ...user,
        roleName: role?.name || resolvedRoleName,
        roleLabel: roleLabelMap[role?.name || resolvedRoleName] || role?.name || resolvedRoleName || '-',
        companyId: resolvedCompanyId,
        companyName: company?.name || '-'
      };
    });

    setUsers(usersWithMeta);
    setRoles(fetchedRoles);
    setCompanies(fetchedCompanies);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await fetchUsersAndRoles();
      } catch (error) {
        console.error('Kullan\u0131c\u0131 verileri y\u00fcklenemedi:', error);
        setErrorMessage('Kullan\u0131c\u0131 verileri y\u00fcklenemedi. L\u00fctfen tekrar deneyin.');
      }
    };

    load();
  }, []);

  const validatePassword = (password) => /^(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(password);
  const validateTc = (tc) => /^[0-9]{11}$/.test(tc);
  const validatePhoneNumber = (phoneNumber) => /^[0-9]{10,15}$/.test(phoneNumber);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'roleId' ? { companyId: '' } : {})
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!validatePassword(formData.password)) {
      setPasswordError('\u015eifre en az 8 karakter, bir b\u00fcy\u00fck harf ve bir k\u00fc\u00e7\u00fck harf i\u00e7ermelidir.');
      return;
    }

    setPasswordError('');

    if (!validateTc(formData.tc)) {
      setErrorMessage('Ge\u00e7erli bir TC Kimlik No giriniz.');
      return;
    }

    if (!validatePhoneNumber(formData.phoneNumber)) {
      setErrorMessage('Ge\u00e7erli bir telefon numaras\u0131 giriniz.');
      return;
    }

    if (isCompanyUserRole && !formData.companyId) {
      setErrorMessage('Firma kullan\u0131c\u0131s\u0131 i\u00e7in firma se\u00e7imi yap\u0131n\u0131z.');
      return;
    }

    try {
      await api.post('/User', {
        ...formData,
        companyId: isCompanyUserRole ? formData.companyId : null
      });

      setSuccessMessage('Kullan\u0131c\u0131 ba\u015far\u0131yla eklendi.');
      setFormData(emptyForm);
      await fetchUsersAndRoles();
      setCurrentPage(1);
    } catch (error) {
      console.error('Kullan\u0131c\u0131 eklenemedi:', error?.response?.data || error);
      setErrorMessage('Kullan\u0131c\u0131 eklenemedi. L\u00fctfen bilgileri kontrol edin.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete('/User', {
        data: { id }
      });
      setUsers((prev) => prev.filter((user) => user.id !== id));
      setSuccessMessage('Kullan\u0131c\u0131 silindi.');
      setShowModal(false);
    } catch (error) {
      console.error('Kullan\u0131c\u0131 silinemedi:', error);
      setErrorMessage('Kullan\u0131c\u0131 silinemedi. L\u00fctfen tekrar deneyin.');
    }
  };

  const openResetModal = (user) => {
    setResetUser(user);
    setResetPassword('');
    setResetPasswordConfirm('');
    setPasswordError('');
    setErrorMessage('');
    setSuccessMessage('');
    setShowResetModal(true);
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setPasswordError('');

    if (!resetUser?.email) {
      setErrorMessage('Kullan\u0131c\u0131 e-postas\u0131 bulunamad\u0131.');
      return;
    }

    if (!validatePassword(resetPassword)) {
      setPasswordError('\u015eifre en az 8 karakter, bir b\u00fcy\u00fck harf ve bir k\u00fc\u00e7\u00fck harf i\u00e7ermelidir.');
      return;
    }

    if (resetPassword !== resetPasswordConfirm) {
      setPasswordError('Yeni \u015fifre ve tekrar \u015fifre e\u015fle\u015fmiyor.');
      return;
    }

    try {
      setResettingPassword(true);
      await api.post('/Auth/ResetPassword', {
        email: resetUser.email,
        password: resetPassword
      });
      setSuccessMessage(`${resetUser.email} i\u00e7in \u015fifre s\u0131f\u0131rland\u0131.`);
      setShowResetModal(false);
      setResetUser(null);
      setResetPassword('');
      setResetPasswordConfirm('');
    } catch (error) {
      console.error('\u015eifre s\u0131f\u0131rlanamad\u0131:', error);
      setErrorMessage('\u015eifre s\u0131f\u0131rlanamad\u0131. L\u00fctfen tekrar deneyin.');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/edit-user/${id}`);
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const getRoleGroupMatch = (user) => {
    if (selectedRoleFilter === 'all') return true;
    if (selectedRoleFilter === 'accounting') return ['Accountant', 'RentalAccountant', 'TechnicalServiceAccountant'].includes(user.roleName);
    if (selectedRoleFilter === 'technical') {
      return ['TechnicalServiceManager', 'TechnicalServicePersonnel', 'TechnicalServiceAccountant'].includes(user.roleName);
    }
    if (selectedRoleFilter === 'company') return user.roleName === 'Company';
    if (selectedRoleFilter === 'internal') return ['Owner', 'Employee'].includes(user.roleName);
    return true;
  };

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.name || ''} ${user.surname || ''}`.trim().toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      !search ||
      fullName.includes(search) ||
      (user.email || '').toLowerCase().includes(search) ||
      (user.tc || '').includes(search);

    const matchesCompany =
      selectedCompanyId === 'all' ||
      (selectedCompanyId === 'none' ? !user.companyId : user.companyId === selectedCompanyId);

    return matchesSearch && matchesCompany && getRoleGroupMatch(user);
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const pagedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompanyId, selectedRoleFilter]);

  const handleKeyPress = (event) => {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  };

  return (
    <div className="user-settings-page">
      <Navbar />

      <div className="user-settings-container">
        <section className="settings-card list-card">
          <div className="card-header list-header">
            <h2>{'Kullan\u0131c\u0131 Listesi'}</h2>
            <span>{filteredUsers.length} {'kay\u0131t'}</span>
          </div>

          <div className="filters">
            <input
              type="text"
              placeholder="Ad, e-posta veya TC ile ara..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />

            <select value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)}>
              <option value="all">{'T\u00fcm Firmalar'}</option>
              <option value="none">{'Firmas\u0131z'}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>

            <select value={selectedRoleFilter} onChange={(event) => setSelectedRoleFilter(event.target.value)}>
              {roleFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>E-posta</th>
                  <th>Rol</th>
                  <th>Firma</th>
                  <th>{'\u0130\u015flemler'}</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.length > 0 ? (
                  pagedUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{`${user.name || ''} ${user.surname || ''}`.trim()}</td>
                      <td>{user.email}</td>
                      <td>{user.roleLabel}</td>
                      <td>{user.companyName}</td>
                      <td>
                        <button className="edit-btn" onClick={() => handleEdit(user.id)}>
                          <i className="fas fa-pencil-alt" />
                        </button>
                        <button className="reset-btn" onClick={() => openResetModal(user)} title={'\u015eifre s\u0131f\u0131rla'}>
                          <i className="fas fa-key" />
                        </button>
                        <button className="delete-btn" onClick={() => openModal(user)}>
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">{'Filtreye uygun kullan\u0131c\u0131 bulunamad\u0131.'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button type="button" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>
              {'\u00d6nceki'}
            </button>
            <span>Sayfa {currentPage} / {totalPages}</span>
            <button type="button" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>
              Sonraki
            </button>
          </div>
        </section>

        <section className="settings-card form-card">
          <div className="card-header">
            <h2>{'Kullan\u0131c\u0131 Ayarlar\u0131'}</h2>
          </div>

          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}

          <form onSubmit={handleSubmit} className="user-settings-form">
            <div className="form-row">
              <div className="form-group">
                <label>{'\u00c7al\u0131\u015fan Ad\u0131'}</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={'\u00c7al\u0131\u015fan Ad\u0131'} required />
              </div>
              <div className="form-group">
                <label>{'\u00c7al\u0131\u015fan Soyad\u0131'}</label>
                <input type="text" name="surname" value={formData.surname} onChange={handleInputChange} placeholder={'\u00c7al\u0131\u015fan Soyad\u0131'} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>TC Kimlik No</label>
                <input
                  type="text"
                  name="tc"
                  value={formData.tc}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  maxLength="11"
                  placeholder="TC Kimlik No"
                  required
                />
              </div>
              <div className="form-group">
                <label>{'Rol Se\u00e7in'}</label>
                <select name="roleId" value={formData.roleId} onChange={handleInputChange} required>
                  <option value="">{'Rol Se\u00e7in'}</option>
                  {sortedRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {roleLabelMap[role.name] || role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isCompanyUserRole && (
              <div className="form-row company-select-row">
                <div className="form-group form-group-spacer" aria-hidden="true" />
                <div className="form-group">
                  <label>{'Firma Se\u00e7in'}</label>
                  <select name="companyId" value={formData.companyId} onChange={handleInputChange} required>
                    <option value="">{'Firma Se\u00e7in'}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>E-posta</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="E-posta" required />
              </div>
              <div className="form-group password-group">
                <label>{'\u015eifre'}</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={'\u015eifre'}
                  required
                />
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} password-icon`} onClick={() => setShowPassword((prev) => !prev)} />
                {passwordError && <small className="password-error">{passwordError}</small>}
              </div>
            </div>

            <div className="form-row single-column">
              <div className="form-group">
                <label>Telefon</label>
                <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder="Telefon" required />
              </div>
            </div>

            <button type="submit" className="primary-btn">{'Kullan\u0131c\u0131 Kaydet'}</button>
          </form>
        </section>
      </div>

      {showModal && selectedUser && (
        <div className="modal">
          <div className="modal-content">
            <h4>{`${selectedUser.name} ${selectedUser.surname}`} {'adl\u0131 kullan\u0131c\u0131y\u0131 silmek istedi\u011finizden emin misiniz?'}</h4>
            <div className="modal-buttons">
              <button className="confirm-btn" onClick={() => handleDelete(selectedUser.id)}>Evet</button>
              <button className="cancel-btn" onClick={() => setShowModal(false)}>{'Hay\u0131r'}</button>
            </div>
          </div>
        </div>
      )}

      {showResetModal && resetUser && (
        <div className="modal">
          <div className="modal-content reset-modal-content">
            <h3>{'\u015eifre S\u0131f\u0131rla'}</h3>
            <p>
              <strong>{`${resetUser.name || ''} ${resetUser.surname || ''}`.trim() || resetUser.email}</strong>
            </p>
            <form onSubmit={handleResetPassword} className="reset-password-form">
              <label>
                {'Yeni \u015eifre'}
                <input
                  type="password"
                  value={resetPassword}
                  onChange={(event) => setResetPassword(event.target.value)}
                  placeholder={'Yeni \u015fifre'}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label>
                {'Yeni \u015eifre Tekrar'}
                <input
                  type="password"
                  value={resetPasswordConfirm}
                  onChange={(event) => setResetPasswordConfirm(event.target.value)}
                  placeholder={'Yeni \u015fifre tekrar'}
                  autoComplete="new-password"
                  required
                />
              </label>
              {passwordError && <small className="password-error">{passwordError}</small>}
              <div className="modal-buttons">
                <button type="submit" className="confirm-btn" disabled={resettingPassword}>
                  {resettingPassword ? 'S\u0131f\u0131rlan\u0131yor...' : '\u015e\u0130FREY\u0130 SIFIRLA'}
                </button>
                <button type="button" className="cancel-btn" onClick={() => setShowResetModal(false)}>
                  {'Vazge\u00e7'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
